import {
  Box,
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  Th,
  Tooltip,
} from "@chakra-ui/react";
import { t } from "i18next";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FaArrowDown, FaArrowsAltV, FaArrowUp, FaFilter, FaSearch } from "react-icons/fa";
import { SortDirection } from "../../autogen/bff-api";
import { SelectorValue } from "../input/Selector/SelectorValue";
import { EntityTableColumn } from "./EntityTable";

export const ColumnHeader = ({
  column,
  debounceMs = 300,
  onDropdownOpen,
  onDropdownClose,
}: {
  column: EntityTableColumn;
  debounceMs?: number;
  onDropdownOpen?: (dropdownKey: string) => void;
  onDropdownClose?: (dropdownKey: string) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState(column.currentSearchValue || "");
  const multiSelectValues = column.multiSelectConfig?.selectedValues || [];
  const singleSelectValue = column.singleSelectConfig?.selectedValue || null;

  const [multiSelectSearchTerm, setMultiSelectSearchTerm] = useState("");
  const [singleSelectSearchTerm, setSingleSelectSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState(column.dateRangeConfig?.fromDate || "");
  const [toDate, setToDate] = useState(column.dateRangeConfig?.toDate || "");

  useEffect(() => {
    setSearchTerm(column.currentSearchValue || "");
  }, [column.currentSearchValue]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    column.onSearch?.(value);
  };

  const handleSort = (direction: SortDirection) => {
    column.onSort?.(direction);
  };
  const handleSortToggle = () => {
    if (!column.onSort) return;
    if (!column.currentSortDirection) return handleSort(SortDirection.Asc);
    if (column.currentSortDirection === SortDirection.Asc) return handleSort(SortDirection.Desc);
    return handleSort(undefined as unknown as SortDirection);
  };

  const debouncedMultiSelectInput = useMemo(
    () =>
      debounce((v: string) => {
        column.multiSelectConfig?.onInputChange?.(v);
      }, debounceMs),
    [column.multiSelectConfig?.onInputChange, debounceMs]
  );
  const debouncedSingleSelectInput = useMemo(
    () =>
      debounce((v: string) => {
        column.singleSelectConfig?.onInputChange?.(v);
      }, debounceMs),
    [column.singleSelectConfig?.onInputChange, debounceMs]
  );

  useEffect(() => {
    return () => {
      debouncedMultiSelectInput.cancel();
      debouncedSingleSelectInput.cancel();
    };
  }, [debouncedMultiSelectInput, debouncedSingleSelectInput]);

  const handleMultiSelectSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMultiSelectSearchTerm(value);
    debouncedMultiSelectInput(value);
  };

  const handleSingleSelectSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSingleSelectSearchTerm(value);
    debouncedSingleSelectInput(value);
  };

  const handleMultiSelectChange = (option: SelectorValue, isChecked: boolean) => {
    const baseValues = column.multiSelectConfig?.selectedValues || [];
    const newValues = isChecked ? [...baseValues, option] : baseValues.filter((v) => v.value !== option.value);
    column.multiSelectConfig?.onSelect(newValues);
  };

  const handleSingleSelectChange = (optionValue: string) => {
    const options = column.singleSelectConfig?.options || [];
    const option = options.find((opt) => opt.value === optionValue);
    if (option) {
      column.singleSelectConfig?.onSelect(option);
    }
  };

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value;
    setFromDate(date);
    column.dateRangeConfig?.onFromDateChange(date || undefined);
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value;
    setToDate(date);
    column.dateRangeConfig?.onToDateChange(date || undefined);
  };

  const isMultiSelectOptionChecked = (option: SelectorValue) => {
    return multiSelectValues.some((v) => v.value === option.value);
  };

  // Filter out already selected options from multiselect
  const availableMultiSelectOptions = useMemo(() => {
    if (!column.multiSelectConfig) return [];
    return column.multiSelectConfig.options.filter(
      (option) => !multiSelectValues.some((selected) => selected.value === option.value)
    );
  }, [column.multiSelectConfig?.options, multiSelectValues]);

  const hasInteractiveFeatures =
    column.onSearch || column.onSort || column.multiSelectConfig || column.singleSelectConfig || column.dateRangeConfig;

  const shouldShowMultiSelectSearch = !!column.multiSelectConfig?.onInputChange;
  const shouldShowSingleSelectSearch = !!column.singleSelectConfig?.onInputChange;

  const getSortIcon = () => {
    if (column.currentSortDirection === SortDirection.Asc) return FaArrowUp;
    if (column.currentSortDirection === SortDirection.Desc) return FaArrowDown;
    return FaArrowsAltV;
  };

  const getSortIconColor = () => {
    return column.currentSortDirection ? "smPrimary" : "smMuted";
  };

  const hasActiveSearch = () => {
    return column.currentSearchValue && column.currentSearchValue.trim() !== "";
  };

  const hasActiveMultiSelect = () => {
    return multiSelectValues && multiSelectValues.length > 0;
  };

  const hasActiveSingleSelect = () => {
    return singleSelectValue && singleSelectValue.value !== "";
  };

  const hasActiveDateRange = () => {
    return (fromDate && fromDate.trim() !== "") || (toDate && toDate.trim() !== "");
  };

  const getSearchIconColor = () => {
    return hasActiveSearch() ? "smPrimary" : "smMuted";
  };

  const getFilterIconColor = () => {
    if (typeof column.isFilterActive === "boolean") {
      return column.isFilterActive ? "smPrimary" : "smMuted";
    }
    if (column.multiSelectConfig) {
      return hasActiveMultiSelect() ? "smPrimary" : "smMuted";
    }
    if (column.singleSelectConfig) {
      return hasActiveSingleSelect() ? "smPrimary" : "smMuted";
    }
    if (column.dateRangeConfig) {
      return hasActiveDateRange() ? "smPrimary" : "smMuted";
    }
    return "smMuted";
  };

  return (
    <Th key={column.key} width={column.width} minWidth={column.width} maxWidth={column.width} py="0">
      {hasInteractiveFeatures ? (
        <Flex
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          px={2}
          py={1}
          _hover={{ bg: "smBackgroundSecondary" }}
          borderRadius="sm"
        >
          <Text>{column.label}</Text>

          <Flex alignItems="center" gap={1}>
            {column.onSearch && (
              <Menu
                onOpen={() => {
                  setSearchTerm(column.currentSearchValue || "");
                  onDropdownOpen?.(`${column.key}-search`);
                }}
                onClose={() => onDropdownClose?.(`${column.key}-search`)}
              >
                <Tooltip label={t("Search")}>
                  <MenuButton
                    as={IconButton}
                    aria-label="Search"
                    icon={<Icon as={FaSearch} />}
                    size="xs"
                    variant="ghost"
                    color={getSearchIconColor()}
                  />
                </Tooltip>
                <MenuList maxHeight="96" overflowY="auto">
                  <Box px={3} py={2}>
                    <InputGroup size="sm">
                      <InputLeftElement>
                        <Icon as={FaSearch} color="smMuted" />
                      </InputLeftElement>
                      <Input placeholder={`${t("Search")}...`} value={searchTerm} onChange={handleSearchChange} />
                    </InputGroup>
                  </Box>
                </MenuList>
              </Menu>
            )}

            {(column.multiSelectConfig || column.singleSelectConfig || column.dateRangeConfig) && (
              <Menu
                onOpen={() => onDropdownOpen?.(`${column.key}-filter`)}
                onClose={() => {
                  setMultiSelectSearchTerm("");
                  setSingleSelectSearchTerm("");
                  onDropdownClose?.(`${column.key}-filter`);
                }}
              >
                <Tooltip label={t("Filter")}>
                  <MenuButton
                    as={IconButton}
                    aria-label="Filter"
                    icon={<Icon as={FaFilter} />}
                    size="xs"
                    variant="ghost"
                    color={getFilterIconColor()}
                  />
                </Tooltip>
                <MenuList maxHeight="96" overflowY="auto">
                  {column.multiSelectConfig && (
                    <Box px={3} py={2} borderBottom="1px solid" borderColor="smBorder">
                      {shouldShowMultiSelectSearch && (
                        <InputGroup size="sm" mb={2}>
                          <InputLeftElement>
                            <Icon as={FaSearch} color="smMuted" />
                          </InputLeftElement>
                          <Input
                            placeholder={`${t("Search options")}...`}
                            value={multiSelectSearchTerm}
                            onChange={handleMultiSelectSearch}
                          />
                        </InputGroup>
                      )}
                      {multiSelectValues.length > 0 && (
                        <Box pb={2}>
                          <Stack spacing={1}>
                            {multiSelectValues.map((selectedValue) => (
                              <Flex
                                key={selectedValue.value}
                                alignItems="center"
                                gap={1}
                                cursor="pointer"
                                onClick={() => handleMultiSelectChange(selectedValue, false)}
                                _hover={{ bg: "smBackgroundSecondary" }}
                                borderRadius="sm"
                              >
                                <Text
                                  fontSize="xs"
                                  maxWidth="60"
                                  whiteSpace="nowrap"
                                  overflowX="hidden"
                                  textOverflow="ellipsis"
                                  textTransform="none"
                                >
                                  {selectedValue.label}
                                </Text>
                                <Text fontSize="xs" color="red.500">
                                  ×
                                </Text>
                              </Flex>
                            ))}
                          </Stack>
                        </Box>
                      )}
                      <Box maxHeight="48" overflowY="auto">
                        {availableMultiSelectOptions.length > 0 ? (
                          availableMultiSelectOptions.map((option) => (
                            <Flex key={option.value} alignItems="center" gap={2}>
                              <Checkbox
                                isChecked={isMultiSelectOptionChecked(option)}
                                onChange={(e) => handleMultiSelectChange(option, e.target.checked)}
                                size="sm"
                              />
                              <Text
                                fontSize="sm"
                                maxWidth="60"
                                whiteSpace="nowrap"
                                overflowX="hidden"
                                overflowY="hidden"
                                textOverflow="ellipsis"
                                cursor="pointer"
                                textTransform="none"
                                onClick={() => handleMultiSelectChange(option, !isMultiSelectOptionChecked(option))}
                              >
                                {option.label}
                              </Text>
                            </Flex>
                          ))
                        ) : column?.multiSelectConfig?.isLoading ||
                          column?.multiSelectConfig?.selectedValuesLoading ? null : (
                          <Text fontSize="sm" color="smMuted" textTransform="none" fontWeight="normal" p={2}>
                            {multiSelectValues.length > 0 ? t("All options selected") : t("No options found")}...
                          </Text>
                        )}
                      </Box>
                      {column.multiSelectConfig.isLoading || column.multiSelectConfig.selectedValuesLoading ? (
                        <Flex width="full" justifyContent="center" pt="2">
                          <Spinner size="sm" color="smMuted" />
                        </Flex>
                      ) : column.multiSelectConfig.optionsCount ? (
                        <Flex justifyContent="center" pt="1">
                          <Text fontSize="xs" color="smMuted" fontWeight="normal" textTransform="none">
                            {column.multiSelectConfig.options.length} {t("out of")}{" "}
                            {column.multiSelectConfig.optionsCount}
                          </Text>
                        </Flex>
                      ) : null}
                    </Box>
                  )}
                  {column.singleSelectConfig && (
                    <Box px={3} py={2}>
                      <Text fontSize="sm" fontWeight="medium" mb={2} color="smMuted">
                        {t("Select value")}
                      </Text>
                      {shouldShowSingleSelectSearch && (
                        <InputGroup size="sm" mb={2}>
                          <InputLeftElement>
                            <Icon as={FaSearch} color="smMuted" />
                          </InputLeftElement>
                          <Input
                            placeholder={`${t("Search options")}...`}
                            value={singleSelectSearchTerm}
                            onChange={handleSingleSelectSearch}
                          />
                        </InputGroup>
                      )}
                      {singleSelectValue && (
                        <Box mb={2}>
                          <Text fontSize="xs" mb={1} color="smMuted">
                            {t("Selected")}:
                          </Text>
                          <Flex
                            alignItems="center"
                            gap={1}
                            cursor="pointer"
                            onClick={() => {
                              column.singleSelectConfig?.onSelect({ value: "", label: "" });
                            }}
                            _hover={{ bg: "smBackgroundSecondary" }}
                            p={1}
                            borderRadius="sm"
                          >
                            <Text
                              fontSize="xs"
                              maxWidth="60"
                              whiteSpace="nowrap"
                              overflowX="hidden"
                              textOverflow="ellipsis"
                            >
                              {singleSelectValue.label}
                            </Text>
                            <Text fontSize="xs" color="red.500">
                              ×
                            </Text>
                          </Flex>
                        </Box>
                      )}
                      <RadioGroup value={singleSelectValue?.value || ""} onChange={handleSingleSelectChange}>
                        <Box maxHeight="48" overflowY="auto">
                          {column.singleSelectConfig.options.length > 0 ? (
                            column.singleSelectConfig.options.map((option) => (
                              <Box key={option.value} pb={0}>
                                <Radio value={option.value} size="sm">
                                  <Text fontSize="sm" textTransform="none">
                                    {option.label}
                                  </Text>
                                </Radio>
                              </Box>
                            ))
                          ) : (
                            <Text fontSize="sm" color="smMuted" textTransform="none" fontWeight="normal" p={2}>
                              {t("No options found")}...
                            </Text>
                          )}
                        </Box>
                      </RadioGroup>
                      {column.singleSelectConfig.isLoading ? (
                        <Flex width="full" justifyContent="center" pt="2">
                          <Spinner size="sm" color="smMuted" />
                        </Flex>
                      ) : column.singleSelectConfig.optionsCount ? (
                        <Text fontSize="xs" color="smMuted" fontWeight="normal" mb={2} textTransform="none">
                          {column.singleSelectConfig.options.length} {t("out of")}{" "}
                          {column.singleSelectConfig.optionsCount}
                        </Text>
                      ) : null}
                    </Box>
                  )}
                  {column.dateRangeConfig && (
                    <Box px={3} py={2} borderBottom="1px solid" borderColor="smBorder">
                      <Text fontSize="sm" fontWeight="medium" mb={2} color="smMuted">
                        {t("Date range")}
                      </Text>
                      <Flex gap={2}>
                        <Input
                          type="date"
                          size="sm"
                          placeholder={column.dateRangeConfig.fromDateLabel || String(t("From"))}
                          value={fromDate || ""}
                          onChange={handleFromDateChange}
                        />
                        <Input
                          type="date"
                          size="sm"
                          placeholder={column.dateRangeConfig.toDateLabel || String(t("To"))}
                          value={toDate || ""}
                          onChange={handleToDateChange}
                        />
                      </Flex>
                    </Box>
                  )}
                </MenuList>
              </Menu>
            )}
            {column.onSort && (
              <Tooltip label={t("Sort")}>
                <IconButton
                  aria-label="Sort"
                  icon={<Icon as={getSortIcon()} />}
                  size="xs"
                  variant="ghost"
                  color={getSortIconColor()}
                  onClick={handleSortToggle}
                />
              </Tooltip>
            )}
          </Flex>
        </Flex>
      ) : (
        <Box p={2}>
          <Flex alignItems="center" gap={1}>
            {column.label}
          </Flex>
        </Box>
      )}
    </Th>
  );
};
